/**
 * Modul-Singleton mit den geladenen Flags, analog zu getPartner()/getUser().
 *
 * Gebraucht ausserhalb von React: trackEvent (exp_* an jedem Event), der
 * Signup-Redirect in auth-container.js (voller Page-Reload, kein Router) und
 * der Funnel beim Schreiben von Form.data.experiments.
 *
 * Solange die Flags nicht geladen sind, liefert alles Control bzw. null. Das
 * ist Absicht: lieber kurz Status quo als ein Flackern zwischen Varianten.
 */
import { getPartner } from '../../helpers/partner'
import { STAGE } from '../../helpers/stage'
import { getUser } from '../../helpers/user'
import { evaluateFlag, variantKeys } from './bucketing'
import { getVisitorId } from './visitor-id'

const QA_STORAGE_KEY = 'aivy_ff_overrides'

/**
 * Flags, bei denen ?ff_<key>= und der gespeicherte Wert unter
 * aivy_ff_overrides fuer niemanden wirken. pricing_enforcement schaltet
 * Sperren im Kunden-Dashboard, ein Kunde darf sie nicht per URL abschalten.
 * Admins testen ueber den Pricing-Override im Profilmenue.
 */
export const QA_OVERRIDE_DENYLIST = new Set(['pricing_enforcement'])

let flags = {}
let source = null

const readParams = () => {
  try {
    return new URLSearchParams(window.location.search)
  } catch {
    return new URLSearchParams()
  }
}

// ?ff_<key>=<variant> fuer QA. Gilt fuer die Tab-Session, zaehlt nie als
// Exposure. sessionStorage, damit die Variante beim Navigieren im Funnel bleibt.
const readQaOverrides = () => {
  let stored = {}
  try {
    stored = JSON.parse(window.sessionStorage.getItem(QA_STORAGE_KEY)) || {}
  } catch {
    stored = {}
  }
  if (typeof stored !== 'object' || Array.isArray(stored)) stored = {}

  let changed = false
  readParams().forEach((value, name) => {
    if (!name.startsWith('ff_')) return
    const key = name.slice(3)
    // Gesperrte Keys gar nicht erst speichern, damit sie nicht in der Session
    // haengen bleiben. Gegriffen wird ohnehin nie (s. qaOverrideFor).
    if (QA_OVERRIDE_DENYLIST.has(key)) return
    stored[key] = value
    changed = true
  })

  if (changed) {
    try {
      window.sessionStorage.setItem(QA_STORAGE_KEY, JSON.stringify(stored))
    } catch {
      // ohne sessionStorage gilt der Override nur fuer diesen Aufruf
    }
  }

  return stored
}

let qaOverrides = null
const getQaOverrides = () => {
  if (!qaOverrides) qaOverrides = readQaOverrides()
  return qaOverrides
}

// Auch ein Wert, der schon vor der Denylist in der Session lag, bleibt wirkungslos
const qaOverrideFor = (key) =>
  QA_OVERRIDE_DENYLIST.has(key) ? undefined : getQaOverrides()[key]

// ?exp_<key>=<variant> von der Landingpage. Die LP hat zugeteilt und die
// Exposure geschrieben, der Funnel uebernimmt nur die Variante fuer den Lead.
let lpAssignments = null
const getLpAssignments = () => {
  if (!lpAssignments) {
    lpAssignments = {}
    readParams().forEach((value, name) => {
      if (name.startsWith('exp_')) lpAssignments[name.slice(4)] = value
    })
  }
  return lpAssignments
}

export const setFlags = (next) => {
  // Gleiche Quelle (react-query-Daten) nicht erneut normalisieren. Der
  // Provider ruft das bei jedem Render auf, auch nach Restore aus dem
  // persistierten Cache, wo die queryFn nicht laeuft.
  if (next === source) return
  source = next
  const normalized = {}
  Object.entries(next || {}).forEach(([key, flag]) => {
    if (flag && typeof flag === 'object') normalized[key] = { ...flag, key }
  })
  flags = normalized
}

export const getFlags = () => flags

/**
 * Kontext fuer die Zuteilung. Felder in `overrides` gewinnen gegen die
 * Singletons. Das braucht es fuer partnerId im ersten Render, denn der
 * StartupHandler setzt das Partner-Singleton erst in einem Effect.
 */
export const buildContext = (overrides = {}) => {
  const partner = getPartner()
  const user = getUser()

  return {
    visitorId: getVisitorId(),
    partnerId: partner?.id || null,
    userId: user?.id || null,
    leadAmpel: partner?.lead_ampel || null,
    isTrial: !!partner?.is_trial,
    stage: STAGE,
    ...overrides
  }
}

/**
 * @returns {{ variant: string|null, tracked: boolean, reason: string }}
 */
export const resolveFlag = (key, ctx = buildContext()) => {
  const flag = flags[key]
  const keys = variantKeys(flag)

  const qa = qaOverrideFor(key)
  if (flag && keys.includes(qa)) {
    return { variant: qa, tracked: false, reason: 'qa' }
  }

  // LP-Experimente teilt nur die LP zu. Ohne Parameter kam der Besucher nicht
  // ueber die LP (Direktaufruf /funnel) und gehoert zu keiner Gruppe.
  if (flag?.surface === 'lp') {
    const fromLp = getLpAssignments()[key]
    return keys.includes(fromLp)
      ? { variant: fromLp, tracked: false, reason: 'landingpage' }
      : { variant: null, tracked: false, reason: 'not_via_landingpage' }
  }

  return evaluateFlag(flag, ctx)
}

export const getVariantSync = (key, ctx) => resolveFlag(key, ctx).variant

/**
 * Alle aktiven Zuteilungen als { key: variant }. Fuer Form.data.experiments
 * und die exp_*-Properties an Events. Nur laufende Experimente, Rollouts und
 * beendete Tests sind fuer die Auswertung uninteressant.
 */
export const activeAssignments = (ctx = buildContext()) => {
  const result = {}
  Object.values(flags).forEach((flag) => {
    if (flag.kind !== 'experiment' || flag.status !== 'running') return
    const { variant } = resolveFlag(flag.key, ctx)
    if (variant) result[flag.key] = variant
  })
  return result
}

// Nur fuer Tests
export const resetStoreForTests = () => {
  flags = {}
  source = null
  qaOverrides = null
  lpAssignments = null
}
